'use client'

import { useState, useRef, useEffect } from 'react'
import { Menu, X, ChevronDown } from 'lucide-react'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { useLocalizedPath } from '@/hooks/useLocalizedPath'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { siteConfig } from '@/config/site'

interface NavigationProps {
  className?: string
  isHomePage?: boolean
}

export function Navigation({ className = '', isHomePage = false }: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isChallenge100Open, setIsChallenge100Open] = useState(false)
  const [isResourcesOpen, setIsResourcesOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const resourcesDropdownRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const { getLocalizedPath } = useLocalizedPath()
  const location = useLocation()
  const navigate = useNavigate()

  // Handle contact link click
  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault()
    const currentPath = location.pathname
    const homePath = getLocalizedPath('/')
    
    if (currentPath === homePath) {
      // If already on homepage, just scroll to contact
      const contactSection = document.getElementById('contact')
      contactSection?.scrollIntoView({ behavior: 'smooth' })
    } else {
      // If on different page, navigate to homepage then scroll to contact
      navigate({ to: homePath })
      // Use setTimeout to ensure navigation completes before scrolling
      setTimeout(() => {
        const contactSection = document.getElementById('contact')
        contactSection?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }

  // Track scroll state internally
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsChallenge100Open(false)
      }
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(event.target as Node)) {
        setIsResourcesOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsChallenge100Open(false)
    setIsResourcesOpen(false)
  }, [location.pathname])

  // Handle body scroll lock when mobile menu is open (only for home page)
  useEffect(() => {
    if (isHomePage && isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen, isHomePage])

  // 使用 siteConfig 中的导航配置
  const navItems = siteConfig.nav.main.map(item => ({
    label: t.nav[item.key as keyof typeof t.nav],
    href: item.href.startsWith('#') ? item.href : getLocalizedPath(item.href),
    isContact: item.key === 'contact'
  }))

  const challenge100Items = siteConfig.nav.challenge100.map(item => ({
    label: t.nav[item.key as keyof typeof t.nav],
    href: getLocalizedPath(item.href)
  }))

  const resourcesItems = siteConfig.nav.resources.map(item => ({
    label: t.nav[item.key as keyof typeof t.nav],
    href: getLocalizedPath(item.href)
  }))

  // 根据是否为首页和滚动状态决定样式
  const getNavStyles = () => {
    if (isHomePage) {
      return {
        background: isScrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' : 'bg-transparent',
        textColor: 'text-white',
        hoverColor: 'hover:text-white/80',
        logoColor: 'text-white',
        dropdownBg: 'bg-black/90 backdrop-blur-md border-white/10',
        dropdownHover: 'hover:bg-white/10',
        mobileBg: 'bg-black/90 backdrop-blur-xl border-l border-white/10',
        mobileHover: 'hover:bg-white/10'
      }
    } else {
      return {
        background: isScrolled ? 'bg-background/95 backdrop-blur-md border-b border-border' : 'bg-background/95 backdrop-blur-md border-b border-border',
        textColor: 'text-foreground/80',
        hoverColor: 'hover:text-foreground',
        logoColor: 'text-foreground',
        dropdownBg: 'bg-card border-border',
        dropdownHover: 'hover:bg-muted',
        mobileBg: 'bg-background/95 backdrop-blur-md',
        mobileHover: 'hover:text-foreground'
      }
    }
  }

  const styles = getNavStyles()

  return (
    <nav 
      className={`navigation-fixed transition-all duration-300 ${styles.background} ${className} overflow-visible`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-6 sm:px-8 lg:px-12 overflow-visible">
        <div className="flex items-center justify-between h-20 overflow-visible">
          
          {/* Logo */}
          <Link 
            to={getLocalizedPath('/')} 
            className={`font-bagel ${styles.logoColor} text-2xl sm:text-3xl tracking-wider hover:opacity-80 transition-opacity z-10 flex-shrink-0`}
          >
            borninsea
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8 overflow-visible">
            {navItems.map((item) => (
              item.isContact ? (
                <button
                  key={item.href}
                  onClick={handleContactClick}
                  className={`${styles.textColor} ${styles.hoverColor} transition-colors font-medium whitespace-nowrap cursor-pointer`}
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`${styles.textColor} ${styles.hoverColor} transition-colors font-medium whitespace-nowrap`}
                >
                  {item.label}
                </Link>
              )
            ))}

            {/* Challenge 100 Dropdown */}
            <div className="relative z-50 overflow-visible" ref={dropdownRef}>
              <button
                onClick={() => setIsChallenge100Open(!isChallenge100Open)}
                className={`flex items-center gap-1 ${styles.textColor} ${styles.hoverColor} transition-colors font-medium whitespace-nowrap`}
                aria-expanded={isChallenge100Open}
                aria-haspopup="true"
              >
                {t.nav.challenge100}
                <ChevronDown className={`w-4 h-4 transition-transform ${isChallenge100Open ? 'rotate-180' : ''}`} />
              </button>

              {isChallenge100Open && (
                <div className={`absolute top-full right-0 mt-2 w-48 ${styles.dropdownBg} border rounded-lg shadow-xl py-2 z-[200] overflow-visible`}>
                  <Link
                    to={getLocalizedPath('/challenge100')}
                    onClick={() => setIsChallenge100Open(false)}
                    className={`block px-4 py-2 text-sm ${styles.textColor} ${styles.hoverColor} ${styles.dropdownHover} transition-colors whitespace-nowrap`}
                  >
                    Overview
                  </Link>
                  <div className={`border-t ${isHomePage ? 'border-white/10' : 'border-border'} my-1`} />
                  {challenge100Items.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setIsChallenge100Open(false)}
                      className={`block px-4 py-2 text-sm ${styles.textColor} ${styles.hoverColor} ${styles.dropdownHover} transition-colors whitespace-nowrap`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Resources Dropdown */}
            <div className="relative z-50 overflow-visible" ref={resourcesDropdownRef}>
              <button
                onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                className={`flex items-center gap-1 ${styles.textColor} ${styles.hoverColor} transition-colors font-medium whitespace-nowrap`}
                aria-expanded={isResourcesOpen}
                aria-haspopup="true"
              >
                {t.nav.resources}
                <ChevronDown className={`w-4 h-4 transition-transform ${isResourcesOpen ? 'rotate-180' : ''}`} />
              </button>

              {isResourcesOpen && (
                <div className={`absolute top-full right-0 mt-2 w-48 ${styles.dropdownBg} border rounded-lg shadow-xl py-2 z-[200] overflow-visible`}>
                  {resourcesItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setIsResourcesOpen(false)}
                      className={`block px-4 py-2 text-sm ${styles.textColor} ${styles.hoverColor} ${styles.dropdownHover} transition-colors whitespace-nowrap`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Projects Link (only for home page) */}
            {isHomePage && (
              <a
                href="#services"
                className={`${styles.textColor} ${styles.hoverColor} transition-colors font-medium whitespace-nowrap`}
              >
                {t.nav.projects}
              </a>
            )}
            {isHomePage && (
              <button
                onClick={() => {
                  const contactSection = document.getElementById('contact')
                  contactSection?.scrollIntoView({ behavior: 'smooth' })
                }}
                className="bg-red-600 backdrop-blur-sm text-white font-semibold px-4 xl:px-6 py-2 xl:py-3 rounded-md hover:bg-red-700 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm xl:text-base flex-shrink-0"
              >
                {t.contact.bookingTitle}
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 ${styles.textColor} ${styles.hoverColor} transition-colors`}
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className={`lg:hidden border-t ${isHomePage ? 'border-white/10' : 'border-border'} ${styles.mobileBg} z-[125] overflow-visible`}>
            <div className="py-4 space-y-2 max-h-[calc(100vh-5rem)] overflow-y-auto">
              {navItems.map((item) => (
                item.isContact ? (
                  <button
                    key={item.href}
                    onClick={(e) => {
                      handleContactClick(e)
                      setIsMobileMenuOpen(false)
                    }}
                    className={`block px-4 py-2 ${styles.textColor} ${styles.mobileHover} transition-colors font-medium w-full text-left`}
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`block px-4 py-2 ${styles.textColor} ${styles.mobileHover} transition-colors font-medium`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              ))}
              
              {/* Challenge 100 Mobile Section */}
              <div className="px-4 py-2">
                <div className={`${isHomePage ? 'text-white/60' : 'text-foreground/60'} text-sm font-medium mb-2`}>{t.nav.challenge100}</div>
                <Link
                  to={getLocalizedPath('/challenge100')}
                  className={`block px-2 py-1 text-sm ${styles.textColor} ${styles.mobileHover} transition-colors`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Overview
                </Link>
                {challenge100Items.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`block px-2 py-1 text-sm ${styles.textColor} ${styles.mobileHover} transition-colors`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {/* Resources Mobile Section */}
              <div className="px-4 py-2">
                <div className={`${isHomePage ? 'text-white/60' : 'text-foreground/60'} text-sm font-medium mb-2`}>{t.nav.resources}</div>
                {resourcesItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`block px-2 py-1 text-sm ${styles.textColor} ${styles.mobileHover} transition-colors`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {/* Projects Link (only for home page) */}
              {isHomePage && (
                <a
                  href="#services"
                  className={`block px-4 py-2 ${styles.textColor} ${styles.mobileHover} transition-colors font-medium`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t.nav.projects}
                </a>
              )}

              {/* Book a Call Button (only for home page) */}
              {isHomePage && (
                <div className="px-4 py-2 mt-4">
                  <button
                    onClick={() => {
                      const contactSection = document.getElementById('contact')
                      contactSection?.scrollIntoView({ behavior: 'smooth' })
                      setIsMobileMenuOpen(false)
                    }}
                    className="w-full bg-red-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-red-700 transition-all duration-300 cursor-pointer"
                  >
                    {t.contact.bookingTitle}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
